import { Box, Typography } from '@mui/material';
import type { KeyboardEvent, PointerEvent, RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResizeObserver } from 'usehooks-ts';
import { siteLookupTokens } from '../siteLookupTokens';
import { chargingPriceDomain, chargingPriceTicks } from './priceBands';

const { priceChart: palette } = siteLookupTokens.color;

/** One week of the chart, oldest first, in USD per kWh. A week without a median is a missing week. */
export type ChargingPriceWeek = {
  weekEnding: string;
  median?: number;
  /** bottom and top edges of the middle 50% band; the band needs both */
  p25?: number;
  p75?: number;
  /** tooltip only */
  min?: number;
  max?: number;
};

// Geometry of the spec, in pixels at 1:1 so that the text stays 11px: the chart is as wide as the card, at most
// 400px (the list column is narrower), with a fixed left margin for the y labels and a small right one so the last
// week's dot and band are not clipped; the legend is below, outside the SVG.
const MAX_WIDTH = 400;
const HEIGHT = 110;
const MARGIN = { left: 38, right: 8 };
const PLOT_Y = { top: 6, bottom: 90 };
const Y_LABEL_X = 32;
const MONTH_Y = 106;
const MONTH_MIN_GAP = 30;
const MIN_BAND_HEIGHT = 2;
const SINGLE_BAND_WIDTH = 12;
const TOOLTIP_OFFSET = 10;

const money = (value: number) => `$${value.toFixed(2)}`;

const hasBand = (week: ChargingPriceWeek): week is ChargingPriceWeek & { p25: number; p75: number } => week.p25 !== undefined && week.p75 !== undefined;

const formatDay = (date: string, locale: string) => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(date));

type Plot = { left: number; right: number; top: number; bottom: number };

/** Plot area of a chart `width` pixels wide. */
const plotArea = (width: number): Plot => ({ left: MARGIN.left, right: width - MARGIN.right, ...PLOT_Y });

/** Point scale with no padding; a single week sits in the middle, as a d3 point scale would. */
const xOf = (plot: Plot, index: number, count: number) =>
  count === 1 ? (plot.left + plot.right) / 2 : plot.left + (index * (plot.right - plot.left)) / (count - 1);

/**
 * Width of the chart: the card width, at most 400px. Measured before the first paint (no flash), then followed with
 * a ResizeObserver; 400 when there is no layout to measure (jsdom).
 */
const useChartWidth = (ref: RefObject<HTMLDivElement | null>) => {
  const [initial, setInitial] = useState<number>();
  useLayoutEffect(() => {
    setInitial(ref.current?.clientWidth || undefined);
  }, [ref]);
  const { width: observed } = useResizeObserver({ ref: ref as RefObject<HTMLDivElement> });
  return Math.min(observed || initial || MAX_WIDTH, MAX_WIDTH);
};

/** Consecutive indices satisfying `has`: a missing week breaks the line and the band. */
const runsOf = (weeks: readonly ChargingPriceWeek[], has: (week: ChargingPriceWeek) => boolean) => {
  const runs: number[][] = [];
  weeks.forEach((week, index) => {
    if (!has(week)) {
      return;
    }
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === index - 1) {
      last.push(index);
    } else {
      runs.push([index]);
    }
  });
  return runs;
};

/** Top and bottom of the band at a week, at least 2px tall and centered on the value when p25 equals p75. */
const bandEdges = (top: number, bottom: number) => {
  const middle = (top + bottom) / 2;
  return { top: Math.min(top, middle - MIN_BAND_HEIGHT / 2), bottom: Math.max(bottom, middle + MIN_BAND_HEIGHT / 2) };
};

const point = (x: number, y: number) => `${+x.toFixed(2)},${+y.toFixed(2)}`;

type Props = { weeks: readonly ChargingPriceWeek[] };

/**
 * Area Summary "Charging price": the weekly median price per kWh as a line over the middle 50% of station prices
 * (p25–p75) as a band, with a legend below. The y-axis fits the band; lowest and highest prices are
 * only in the tooltip. Hovering, tapping or the arrow keys pick a week.
 */
export const ChargingPriceChart = ({ weeks }: Props) => {
  const { t, i18n } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useChartWidth(containerRef);
  const plot = plotArea(width);
  const plotMiddle = (plot.left + plot.right) / 2;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const count = weeks.length;
  const domain = useMemo(() => chargingPriceDomain(weeks), [weeks]);
  // weeks the hover and the arrow keys can reach
  const reachable = useMemo(() => weeks.flatMap((week, index) => (week.median === undefined ? [] : [index])), [weeks]);

  // a tap outside the plot hides the tooltip
  useEffect(() => {
    if (activeIndex === null) {
      return;
    }
    const hide = (event: globalThis.PointerEvent) => {
      if (!svgRef.current?.contains(event.target as Node)) {
        setActiveIndex(null);
      }
    };
    document.addEventListener('pointerdown', hide);
    return () => document.removeEventListener('pointerdown', hide);
  }, [activeIndex]);

  const header = (
    <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1} width={width} marginBottom="8px">
      <Typography component="h3" fontSize={14} fontWeight={500} color={palette.text.primary}>
        {t('siteLookup.detail.priceChart.title')}
      </Typography>
      <Typography fontSize={12} fontWeight={400} color={palette.text.secondary} textAlign="right">
        {t('siteLookup.detail.priceChart.unit')}
      </Typography>
    </Box>
  );

  if (!domain || !reachable.length) {
    return (
      <Box ref={containerRef}>
        {header}
        <Typography fontSize={12} color={palette.text.secondary} textAlign="center" width={width} paddingY={3}>
          {t('siteLookup.detail.priceChart.noData')}
        </Typography>
      </Box>
    );
  }

  const [low, high] = domain;
  const yOf = (value: number) => plot.bottom - ((value - low) / (high - low)) * (plot.bottom - plot.top);
  const locale = i18n.language;

  const bandShapes = runsOf(weeks, hasBand).map((run) => {
    const edges = run.map((index) => {
      const week = weeks[index] as ChargingPriceWeek & { p25: number; p75: number };
      return { x: xOf(plot, index, count), ...bandEdges(yOf(week.p75), yOf(week.p25)) };
    });
    if (edges.length === 1) {
      // nothing to join: a short bar centered on the week
      const [{ x, top, bottom }] = edges;
      return { key: run[0], rect: { x: x - SINGLE_BAND_WIDTH / 2, y: top, height: bottom - top } };
    }
    return { key: run[0], points: [...edges.map((e) => point(e.x, e.top)), ...edges.reverse().map((e) => point(e.x, e.bottom))].join(' ') };
  });

  const lineShapes = runsOf(weeks, (week) => week.median !== undefined).map((run) => ({
    key: run[0],
    vertices: run.map((index) => ({ x: xOf(plot, index, count), y: yOf(weeks[index].median as number) })),
  }));

  // month of each week in UTC, like the dates of the tooltip; a label too close to the previous one is skipped
  const monthLabels: { x: number; label: string }[] = [];
  const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const monthKey = (date: string) => new Date(date).toISOString().slice(0, 7);
  weeks.forEach((week, index) => {
    if (index > 0 && monthKey(weeks[index - 1].weekEnding) === monthKey(week.weekEnding)) {
      return;
    }
    const x = xOf(plot, index, count);
    const previous = monthLabels[monthLabels.length - 1];
    if (!previous || x - previous.x >= MONTH_MIN_GAP) {
      monthLabels.push({ x, label: monthFormat.format(new Date(week.weekEnding)) });
    }
  });

  const lastMedianWeek = weeks[reachable[reachable.length - 1]];

  const ariaDate = formatDay(lastMedianWeek.weekEnding, locale);
  const ariaLabel = hasBand(lastMedianWeek)
    ? t('siteLookup.detail.priceChart.ariaLabelWithBand', {
        median: money(lastMedianWeek.median as number),
        date: ariaDate,
        p25: money(lastMedianWeek.p25),
        p75: money(lastMedianWeek.p75),
      })
    : t('siteLookup.detail.priceChart.ariaLabel', { median: money(lastMedianWeek.median as number), date: ariaDate });

  const activate = (event: PointerEvent<SVGRectElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box?.width) {
      return;
    }
    const x = ((event.clientX - box.left) / box.width) * width;
    const nearest = reachable.reduce((best, index) => (Math.abs(xOf(plot, index, count) - x) < Math.abs(xOf(plot, best, count) - x) ? index : best));
    setActiveIndex(nearest);
  };

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key === 'Escape') {
      setActiveIndex(null);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const position = activeIndex === null ? -1 : reachable.indexOf(activeIndex);
    // the first press shows the latest week
    const next = position === -1 ? reachable.length - 1 : Math.max(0, Math.min(reachable.length - 1, position + (event.key === 'ArrowRight' ? 1 : -1)));
    setActiveIndex(reachable[next]);
  };

  const active = activeIndex === null ? null : weeks[activeIndex];
  const activeX = activeIndex === null ? 0 : xOf(plot, activeIndex, count);
  const tooltipPosition = activeX > plotMiddle ? { right: width - activeX + TOOLTIP_OFFSET } : { left: activeX + TOOLTIP_OFFSET };

  return (
    <Box ref={containerRef}>
      {header}
      <Box
        position="relative"
        width={width}
        sx={{
          '& svg': { display: 'block', overflow: 'visible', outline: 'none' },
          '& svg:focus-visible': { outline: `2px solid ${siteLookupTokens.color.primary}`, outlineOffset: 2 },
        }}
      >
        <svg
          ref={svgRef}
          role="img"
          aria-label={ariaLabel}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the plot is explored with the arrow keys
          tabIndex={0}
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          onKeyDown={onKeyDown}
          onBlur={() => setActiveIndex(null)}
        >
          {chargingPriceTicks(domain).map((tick) => (
            <g key={tick}>
              <line x1={plot.left} x2={plot.right} y1={yOf(tick)} y2={yOf(tick)} stroke={palette.grid} strokeWidth={0.5} />
              <text x={Y_LABEL_X} y={yOf(tick)} textAnchor="end" dominantBaseline="central" fontSize={11} fill={palette.text.muted}>
                {money(tick)}
              </text>
            </g>
          ))}
          {monthLabels.map(({ x, label }) => (
            <text key={x} x={x} y={MONTH_Y} textAnchor="middle" fontSize={11} fill={palette.text.muted}>
              {label}
            </text>
          ))}

          <g data-testid="price-band" fill={palette.band} fillOpacity={palette.bandOpacity} stroke="none">
            {bandShapes.map((shape) =>
              shape.rect ? (
                <rect key={shape.key} x={shape.rect.x} y={shape.rect.y} width={SINGLE_BAND_WIDTH} height={shape.rect.height} />
              ) : (
                <polygon key={shape.key} points={shape.points} />
              ),
            )}
          </g>
          <g data-testid="price-median" fill="none" stroke={palette.median} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round">
            {lineShapes.map(({ key, vertices }) =>
              vertices.length > 1 ? (
                <polyline key={key} points={vertices.map((v) => point(v.x, v.y)).join(' ')} />
              ) : count > 1 ? (
                // a week alone between missing weeks: a dot of the line width, so its median is not lost
                <circle key={key} cx={vertices[0].x} cy={vertices[0].y} r={1} fill={palette.median} stroke="none" />
              ) : null,
            )}
          </g>

          {/* a single week has no line: its dot is always drawn */}
          {(active?.median !== undefined || count === 1) && (
            <g pointerEvents="none">
              {active && <line x1={activeX} x2={activeX} y1={plot.top} y2={plot.bottom} stroke={palette.cursor} strokeWidth={1} />}
              <circle
                cx={active ? activeX : plotMiddle}
                cy={yOf((active ?? lastMedianWeek).median as number)}
                r={4}
                fill={palette.median}
                stroke={siteLookupTokens.color.areaSummary}
                strokeWidth={2}
              />
            </g>
          )}
          <rect
            data-testid="price-hit-area"
            x={plot.left}
            y={plot.top}
            width={plot.right - plot.left}
            height={plot.bottom - plot.top}
            fill="transparent"
            style={{ touchAction: 'pan-y' }}
            onPointerDown={activate}
            onPointerMove={activate}
            // a touch ends with a leave: the tooltip stays until a tap elsewhere
            onPointerLeave={(event) => event.pointerType !== 'touch' && setActiveIndex(null)}
          />
        </svg>

        <Box aria-live="polite" position="absolute" top={0} zIndex={1} sx={{ pointerEvents: 'none', ...tooltipPosition }}>
          {active && <PriceTooltip week={active} locale={locale} />}
        </Box>
      </Box>
      <PriceLegend withBand={bandShapes.length > 0} width={width} />
    </Box>
  );
};

/** The series below the chart; the swatches are hidden from screen readers, the accessible name already tells the series. */
const PriceLegend = ({ withBand, width }: { withBand: boolean; width: number }) => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="price-legend"
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      columnGap="16px"
      rowGap="4px"
      width={width}
      boxSizing="border-box"
      marginTop="6px"
      paddingLeft={`${MARGIN.left}px`}
    >
      <Box display="flex" alignItems="center" gap="6px">
        <Box
          aria-hidden
          data-testid="price-legend-median-swatch"
          width={14}
          height={2}
          borderRadius="1px"
          flexShrink={0}
          sx={{ backgroundColor: palette.median }}
        />
        <Typography fontSize={11} fontWeight={500} color={palette.text.primary}>
          {t('siteLookup.detail.priceChart.label.median')}
        </Typography>
      </Box>
      {withBand && (
        <Box display="flex" alignItems="center" gap="6px">
          <Box
            aria-hidden
            data-testid="price-legend-band-swatch"
            width={14}
            height={8}
            borderRadius="2px"
            flexShrink={0}
            sx={{ backgroundColor: palette.band, opacity: palette.bandOpacity }}
          />
          <Typography fontSize={11} fontWeight={400} color={palette.text.secondary}>
            {t('siteLookup.detail.priceChart.label.band')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** The week, its median, the middle 50% and what it means, then the lowest and highest prices. */
const PriceTooltip = ({ week, locale }: { week: ChargingPriceWeek; locale: string }) => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="price-tooltip"
      sx={{
        backgroundColor: palette.tooltipBackground,
        border: `0.5px solid ${palette.tooltipBorder}`,
        borderRadius: '10px',
        padding: '8px 10px',
        minWidth: 150,
        maxWidth: 220,
        width: 'max-content',
        lineHeight: 1.45,
      }}
    >
      <Typography fontSize={11} lineHeight="inherit" color={palette.text.secondary}>
        {t('siteLookup.detail.priceChart.tooltip.date', { date: formatDay(week.weekEnding, locale) })}
      </Typography>
      {week.median !== undefined && (
        <Typography lineHeight="inherit" color={palette.text.primary}>
          <Box component="span" fontSize={16} fontWeight={500}>
            {money(week.median)}
          </Box>
          <Box component="span" fontSize={11} color={palette.text.secondary}>
            {` ${t('siteLookup.detail.priceChart.tooltip.unit')}`}
          </Box>
        </Typography>
      )}
      {hasBand(week) && (
        <>
          <Typography fontSize={12} lineHeight="inherit" color={palette.text.primary}>
            {t('siteLookup.detail.priceChart.tooltip.range', { p25: money(week.p25), p75: money(week.p75) })}
          </Typography>
          <Typography fontSize={11} lineHeight="inherit" color={palette.text.muted} marginTop="2px">
            {t('siteLookup.detail.priceChart.tooltip.rangeHelp')}
          </Typography>
        </>
      )}
      {week.min !== undefined && week.max !== undefined && (
        <Typography fontSize={11} lineHeight="inherit" color={palette.text.muted} marginTop="3px" paddingTop="3px" borderTop={`0.5px solid ${palette.grid}`}>
          {t('siteLookup.detail.priceChart.tooltip.extremes', { min: money(week.min), max: money(week.max) })}
        </Typography>
      )}
    </Box>
  );
};
